// Tryb — field view models.
//
// One field definition plus the current answers becomes a plain object that
// TrybField can render without knowing anything about the schema. This is the
// only place that decides how a type is presented — chips under six options, a
// select above, a switch for a boolean, and so on.

import { useCallback, useMemo } from "react";
import { BY_ID, ENUMS } from "@shared/fields.js";
import { fmtDate, fmtSize, isRequired, isVisible, sessionDates, validate } from "@shared/validation.js";
import { api } from "./api.js";
import { useStudio } from "./studioContext.js";

const CATEGORY_ICONS = {
  Featured: "Sparkle",
  "Movement & fitness": "Users",
  "Food & drink": "Coffee",
  "Craft & making": "Sparkle",
  Music: "Music",
  "Reading & writing": "Book",
  "Games & puzzles": "Grid",
  "Outdoors & nature": "Compass",
  "Learning & skills": "Book",
  "Art & culture": "Star",
  Wellbeing: "Heart",
  Quiet: "Clock",
};

export const COVER_COLOURS = {
  ember: { label: "Ember", bg: "var(--ember-500)", fg: "var(--sand-75)" },
  ink: { label: "Ink", bg: "var(--ink-900)", fg: "var(--sand-75)" },
  navy: { label: "Navy", bg: "var(--navy-700)", fg: "var(--sand-75)" },
  sage: { label: "Sage", bg: "var(--sage-600)", fg: "var(--sand-50)" },
  honey: { label: "Honey", bg: "var(--honey-500)", fg: "var(--ink-900)" },
  sand: { label: "Sand", bg: "var(--sand-200)", fg: "var(--ink-900)" },
};

export const COVER_CATEGORIES = Object.keys(CATEGORY_ICONS).filter((c) => c !== "Quiet" || true);

export function iconForCategory(cat) {
  return CATEGORY_ICONS[cat] || "Users";
}

/** Answers the studio works out rather than asks for. */
function derived(id, vals) {
  if (id === "E.11") return "12% of each booking · weekly series";

  if (id === "C4.08") {
    const [h, m] = String(vals["B2.05"] || "19:00").split(":").map(Number);
    const end = h * 60 + m + (+vals["B2.03"] || 90);
    const t = `${String(Math.floor(end / 60) % 24).padStart(2, "0")}:${String(end % 60).padStart(2, "0")}`;
    return end > 20 * 60 + 30 ? `Yes · wraps at ${t}` : `No · wraps at ${t}`;
  }

  if (id === "D1.02") {
    const ds = sessionDates(vals).filter((x) => !x.skipped);
    return ds.length ? fmtDate(ds[ds.length - 1].date) : "Pick a start date first";
  }

  return vals[id] ?? "—";
}

/**
 * Returns builders for field view models.
 *
 * Fields carry `hint` and `why` copy from the schema, but the studio doesn't
 * render it: a helper line under all 191 questions buried the questions
 * themselves. Labels and placeholders carry the meaning; errors say what to fix.
 */
export function useFieldViews({ onOpenCover } = {}) {
  const studio = useStudio();
  const { vals, documents, verifyStatus, setField, showToast, applyStudio, refresh } = studio;

  const uploadFile = useCallback(
    async (fieldId, file) => {
      try {
        const { answer } = await api.uploadDocument(fieldId, file);
        setField(fieldId, answer);
        await refresh();
        if (fieldId === "A1.07" || fieldId === "A1.11") {
          showToast("Got it — with our team now", "Usually checked within a day. We’ll let you know as soon as it clears.");
        }
      } catch (e) {
        showToast(e.message, e.hint);
      }
    },
    [setField, refresh, showToast],
  );

  const removeFile = useCallback(
    async (fieldId) => {
      try {
        await api.deleteDocument(fieldId);
        setField(fieldId, "");
        await refresh();
      } catch (e) {
        showToast("Couldn’t remove that", e.message);
      }
    },
    [setField, refresh, showToast],
  );

  /** Build the view model for one field id, or null if it is hidden. */
  const fieldView = useCallback(
    (id, showErrors) => {
      const def = BY_ID[id];
      if (!def || !isVisible(def, vals)) return null;

      const v = vals[id];
      const err = validate(def, vals);
      const req = isRequired(def, vals);
      // Who can see an answer is recorded on the field and enforced by the API,
      // but it isn't shown here — facilitators found the OPS ONLY / PUBLIC tags
      // noisy on every single question. Only optionality is worth a label.
      const meta = req ? "" : def.req === "cond" ? "IF IT APPLIES" : "OPTIONAL";
      const f = {
        id,
        label: def.label,
        meta,
        error: showErrors && err ? err : "",
        hasError: !!(showErrors && err),
        cols: 1,
        value: typeof v === "object" && v !== null ? "" : (v ?? ""),
        ph: def.ph || "",
        rows: def.rows || 4,
      };

      const onText = (e) => setField(id, e && e.target ? e.target.value : e);
      const t = def.type;

      if (["text", "email", "phone", "url"].includes(t)) {
        f.isInput = true;
        f.inputType = t === "phone" ? "tel" : t;
        f.onChange = onText;
      } else if (t === "int" || t === "money") {
        f.isInput = true;
        f.inputType = "number";
        f.onChange = (e) => setField(id, e.target.value === "" ? "" : +e.target.value);
      } else if (t === "date" || t === "time") {
        f.isInput = true;
        f.inputType = t;
        f.onChange = onText;
      } else if (t === "enum") {
        const opts = def.dep ? ENUMS.SUBCATEGORY[vals[def.dep]] || [] : ENUMS[def.opts];
        if (def.dep && !vals[def.dep]) {
          f.isRo = true;
          f.value = "Pick a category first";
        } else if (opts.length <= 5) {
          f.isChips = true;
          f.cols = 2;
          f.tags = opts.map((o) => ({ label: o, selected: v === o, onClick: () => setField(id, v === o ? "" : o) }));
        } else {
          f.isSelect = true;
          f.options = opts;
          f.onChange = onText;
        }
      } else if (t === "multi") {
        const opts = ENUMS[def.opts];
        const arr = Array.isArray(v) ? v : [];
        f.isChips = true;
        f.cols = 2;
        f.tags = opts.map((o) => ({
          label: o,
          selected: arr.includes(o),
          onClick: () => setField(id, arr.includes(o) ? arr.filter((x) => x !== o) : [...arr, o]),
        }));
      } else if (t === "bool" || t === "boolfile") {
        f.isSwitch = true;
        f.on = v === true;
        f.onText = v === true ? "Yes" : "No";
        f.onChange = (e) => setField(id, e && e.target ? !!e.target.checked : !v);
      } else if (t === "textarea" || t === "address") {
        f.isTextarea = true;
        f.cols = 2;
        f.rows = def.rows || (t === "address" ? 2 : 4);
        f.onChange = onText;
      } else if (["file", "image", "images"].includes(t)) {
        const doc = v && typeof v === "object" ? v : null;
        const stored = doc && documents[doc.key];

        f.isFile = true;
        f.hasFile = !!doc;
        f.noFile = !doc;
        f.cols = t === "file" && def.gate === "Onboarding" ? 2 : 1;
        f.fileName = doc ? doc.name : "";
        f.fileMeta = doc ? `${fmtSize(doc.size)} · ${String(doc.type).split("/")[1] || "file"} · encrypted, ops only` : "";
        f.thumb = stored?.url || "";
        f.hasThumb = !!f.thumb;
        f.noThumb = !f.thumb;
        f.fileHint = def.hint || (t === "image" ? "JPG or PNG" : "PDF or photo");
        f.accept = t === "file" ? "image/*,.pdf" : "image/*";
        f.onFile = (e) => {
          const file = e.target.files?.[0];
          e.target.value = "";
          if (file) uploadFile(id, file);
        };
        f.onRemove = () => removeFile(id);
      } else if (t === "cover") {
        const c = v && typeof v === "object" ? v : null;
        const col = COVER_COLOURS[c?.color || "sand"];
        const stored = c?.kind === "upload" ? documents[c.key] : null;

        f.isCover = true;
        f.cols = 2;
        f.coverHas = !!c;
        f.coverEmpty = !c;
        f.coverIsUpload = c?.kind === "upload";
        f.coverBg = c?.kind === "template" ? col.bg : "var(--sand-200)";
        f.coverFg = c?.kind === "template" ? col.fg : "var(--ink-900)";
        f.coverShowIcon = c?.kind === "template" && c.style !== "type";
        f.coverShowText = c?.kind === "template" && c.style !== "icon";
        f.coverIcon = c?.icon || "Users";
        f.coverText = c?.text || vals["B1.01"] || "Your tryb";
        f.coverFont = c?.font === "mono" ? "var(--font-mono)" : "var(--font-display)";
        f.thumb = stored?.url || "";
        f.coverCaption = !c
          ? "This is the first thing people see. Your own photo works, or make one from a template — takes a minute."
          : c.kind === "upload"
            ? `${c.name} · ${fmtSize(c.size)}`
            : `Template · ${col.label} · ${c.style === "both" ? "name and icon" : c.style === "type" ? "name only" : "icon only"}`;
        f.coverCta = c ? "Change" : "Choose a cover";
        f.onOpen = () => onOpenCover?.();
        f.onRemove = () => { if (c?.kind === "upload") removeFile(id); else setField(id, ""); };
      } else if (t === "range") {
        const r = v || {};
        f.isRange = true;
        f.lo = r.lo ?? "";
        f.hi = r.hi ?? "";
        f.onLo = (e) => setField(id, { ...r, lo: e.target.value === "" ? "" : +e.target.value });
        f.onHi = (e) => setField(id, { ...r, hi: e.target.value === "" ? "" : +e.target.value });
      } else if (t === "timerange" || t === "namephone") {
        const r = v || {};
        f.isPair = true;
        f.lo = r.lo ?? "";
        f.hi = r.hi ?? "";
        f.pairType = t === "timerange" ? "time" : "text";
        f.pairType2 = t === "timerange" ? "time" : "tel";
        f.loPh = t === "timerange" ? "" : "Their name";
        f.hiPh = t === "timerange" ? "" : "+91";
        f.onLo = (e) => setField(id, { ...r, lo: e.target.value });
        f.onHi = (e) => setField(id, { ...r, hi: e.target.value });
      } else if (t === "geo") {
        f.isGeo = true;
        f.cols = 2;
        f.hasPin = !!v;
        f.pinText = v ? `Pinned · ${v}` : "No pin yet — put it on the actual door";
        f.pinCta = v ? "Move it" : "Drop the pin";
        f.onPin = () => setField(id, v ? "" : "12.9784, 77.6408");
      } else if (t === "dates") {
        f.isDates = true;
        f.cols = 2;
        f.dates = sessionDates(vals).map((x) => ({
          label: x.skipped ? `${fmtDate(x.date)} · skipped` : fmtDate(x.date),
          tone: x.skipped ? "warning" : "neutral",
        }));
        if (!f.dates.length) f.dates = [{ label: "Pick a start date and a weekday first", tone: "neutral" }];
      } else if (t === "ro") {
        f.isRo = true;
        f.value = id === "A1.08" ? verifyStatus : derived(id, vals);
        f.meta = def.derived ? "WORKED OUT FOR YOU" : "SET BY TRYB";
      }

      return f;
    },
    [vals, documents, verifyStatus, setField, uploadFile, removeFile, onOpenCover],
  );

  const fieldViews = useCallback((ids, showErrors) => ids.map((id) => fieldView(id, showErrors)).filter(Boolean), [fieldView]);

  return useMemo(() => ({ fieldView, fieldViews, uploadFile, removeFile }), [fieldView, fieldViews, uploadFile, removeFile]);
}
